import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const hhmm = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const toLocalHM = (iso) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

const SplitControls = ({ seg, split, setSplit }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <Switch data-testid="relabel-split-toggle" checked={split.on} onCheckedChange={(on) => setSplit({ ...split, on })} />
      <span className="text-sm">Split this entry first</span>
    </div>
    {split.on && (
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-500">Split at</Label>
          <Input data-testid="relabel-split-time" type="time" value={split.time} className="mt-1 w-32 mono"
                 onChange={(e) => setSplit({ ...split, time: e.target.value })} />
        </div>
        <div className="w-44">
          <Label className="text-xs text-slate-500">Relabel</Label>
          <Select value={split.part} onValueChange={(part) => setSplit({ ...split, part })}>
            <SelectTrigger data-testid="relabel-split-part" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="before">{hhmm(seg.started_at)} – split</SelectItem>
              <SelectItem value="after">split – {hhmm(seg.stopped_at)}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    )}
  </div>
);

const splitIso = (seg, time) => {
  const [h, m] = time.split(":").map((x) => parseInt(x, 10));
  const d = new Date(seg.started_at);
  d.setHours(h, m, 0, 0);
  if (d <= new Date(seg.started_at)) d.setDate(d.getDate() + 1); // entry crosses midnight
  return d.toISOString();
};

export const RelabelDialog = ({ seg, onClose, onDone }) => {
  const [kind, setKind] = useState("meeting");
  const [reason, setReason] = useState("");
  const [taskId, setTaskId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [split, setSplit] = useState({ on: false, time: "", part: "before" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!seg) return;
    setKind("meeting"); setReason(""); setTaskId("");
    const mid = new Date((new Date(seg.started_at).getTime() + new Date(seg.stopped_at).getTime()) / 2);
    mid.setSeconds(0, 0);
    if (mid <= new Date(seg.started_at)) mid.setMinutes(mid.getMinutes() + 1);
    setSplit({ on: false, time: toLocalHM(mid.toISOString()), part: "before" });
  }, [seg]);
  useEffect(() => {
    if (kind !== "task" || tasks.length) return;
    api.get("/tasks?view=active&scope=mine").then(({ data }) => setTasks(data)).catch((e) => toast.error(formatErr(e)));
  }, [kind, tasks.length]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/activity/segments/${seg.id}/relabel`, {
        kind, reason: reason || null, task_id: kind === "task" ? taskId : null,
        split_at: split.on ? splitIso(seg, split.time) : null, part: split.part,
      });
      toast.success("Entry relabelled");
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!seg} onOpenChange={(o) => !o && onClose()}>
      {seg && (
        <DialogContent className="max-w-md" data-testid="relabel-dialog">
          <DialogHeader>
            <DialogTitle>Review this time</DialogTitle>
            <DialogDescription>
              {hhmm(seg.started_at)} – {hhmm(seg.stopped_at)} · {seg.asleep ? "device asleep" : "Idle"}. What were you doing?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="w-56">
              <Label className="text-xs text-slate-500">Relabel as</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="relabel-kind" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="break">Break</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {kind === "task" ? (
              <div>
                <Label className="text-xs text-slate-500">Task</Label>
                <Select value={taskId} onValueChange={setTaskId}>
                  <SelectTrigger data-testid="relabel-task" className="mt-1"><SelectValue placeholder="Choose a task…" /></SelectTrigger>
                  <SelectContent>
                    {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs text-slate-500">Short note (optional)</Label>
                <Input data-testid="relabel-reason" value={reason} maxLength={200} className="mt-1"
                       placeholder={kind === "break" ? "Lunch" : "Client call with…"} onChange={(e) => setReason(e.target.value)} />
              </div>
            )}
            <SplitControls seg={seg} split={split} setSplit={setSplit} />
          </div>
          <DialogFooter>
            <Button data-testid="relabel-save" disabled={saving || (kind === "task" && !taskId) || (split.on && !split.time)}
                    onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};
